import { useId, type ReactNode } from 'react';

/** Small labelled inputs shared by the proxy admin forms. */

export function TextField({
  label,
  value,
  onChange,
  hint,
  mono = true,
  required = false,
  disabled = false,
  placeholder,
  inputMode,
  list,
  className = 'w-56',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  mono?: boolean;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  inputMode?: 'decimal' | 'numeric';
  list?: string;
  className?: string;
}) {
  const id = useId();
  return (
    // Full width in the narrow bottom sheet; their own width otherwise.
    <div className="flex flex-col gap-1 max-md:w-full">
      <label htmlFor={id} className="text-muted">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        className={`input h-7 text-xs max-md:w-full ${mono ? 'font-mono' : ''} ${className}`}
        value={value}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        inputMode={inputMode}
        list={list}
        title={hint}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  hint,
  required = false,
  className = 'w-56',
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  hint?: string;
  required?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1 max-md:w-full">
      <label htmlFor={id} className="text-muted">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <select
        id={id}
        className={`input h-7 text-xs max-md:w-full ${className}`}
        value={value}
        required={required}
        title={hint}
        onChange={e => onChange(e.target.value as T)}
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
}) {
  const id = useId();
  return (
    <span className="inline-flex h-7 items-center gap-1.5" title={hint}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
    </span>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  hint,
  rows = 3,
  mono = false,
  invalid = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: ReactNode;
  rows?: number;
  mono?: boolean;
  invalid?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-muted">
        {label}
      </label>
      <textarea
        id={id}
        // h-auto: `.input` fixes the height at one line, which overrides `rows`.
        className={`input h-auto w-full max-w-3xl p-1.5 text-xs ${mono ? 'font-mono' : ''}`}
        rows={rows}
        value={value}
        spellCheck={false}
        aria-invalid={invalid || undefined}
        onChange={e => onChange(e.target.value)}
      />
      {hint && <p className="text-muted">{hint}</p>}
    </div>
  );
}

export function Section({
  legend,
  children,
}: {
  legend: string;
  children: ReactNode;
}) {
  return (
    <fieldset className="mt-3">
      <legend className="eyebrow">{legend}</legend>
      <div className="mt-1 flex flex-wrap items-end gap-x-3 gap-y-2">
        {children}
      </div>
    </fieldset>
  );
}
